import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";
import { VEILLE_COMMERCIALE } from "@/config/siteFlags";

const LOGO = "/logo-horsindex.png";

function Brand() {
  return (
    <Link
      to="/"
      data-testid="navbar-logo-link"
      className="flex items-center gap-2.5 shrink-0"
    >
      <div className="inline-flex items-center justify-center overflow-hidden h-10 w-10 lg:h-11 lg:w-11">
        <img
          src={LOGO}
          alt="Logo HorsIndex"
          className="h-10 w-10 lg:h-11 lg:w-11 object-contain"
          style={{ transform: "scale(1.35)", transformOrigin: "center" }}
          data-testid="navbar-logo-img"
        />
      </div>
      <span className="font-sans font-semibold text-xl lg:text-[21px] text-[#181A1D] tracking-tight">
        HorsIndex
      </span>
    </Link>
  );
}

function DesktopNav({ links, isActive }) {
  return (
    <nav
      data-testid="navbar-capsule"
      aria-label="Navigation principale"
      className="hidden xl:flex items-center gap-6"
    >
      <div className="flex items-center gap-1.5 bg-[#F2F0ED]/80 border border-[#E7E3DE] rounded-[12px] px-2 py-1.5">
        {links.map((l) => {
          const active = isActive(l.to);
          return (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              aria-current={active ? "page" : undefined}
              className={`whitespace-nowrap px-4 py-2 text-[15px] font-medium rounded-[8px] transition-colors duration-200 ${
                active
                  ? "bg-[#F6E3E0] text-[#181A1D]"
                  : "text-[#181A1D] hover:bg-[#FAF9F7]"
              }`}
            >
              {l.label}
            </Link>
          );
        })}
      </div>
      {VEILLE_COMMERCIALE ? null : (
        <Link
          to="/contact"
          data-testid="nav-cta-contact"
          className="inline-flex items-center whitespace-nowrap bg-[#E94B3C] text-white text-[13px] font-medium px-4 py-2.5 rounded-md hover:bg-[#B83A34] transition-colors duration-200"
        >
          Prendre contact
        </Link>
      )}
    </nav>
  );
}

function MobileDrawer({ links, isActive, onClose }) {
  return (
    <div
      className="xl:hidden border-t border-[#E2DEDA] bg-[#FAF9F7] max-h-[calc(100vh-4rem)] overflow-y-auto"
      data-testid="navbar-mobile-menu"
    >
      <div className="px-6 py-8 flex flex-col">
        {links.map((l) => {
          const active = isActive(l.to);
          return (
            <Link
              key={l.id}
              to={l.to}
              onClick={onClose}
              data-testid={`nav-mobile-link-${l.id}`}
              aria-current={active ? "page" : undefined}
              className={`flex items-center gap-3 py-3 px-3 -mx-3 rounded-md text-[15px] transition-colors ${
                active
                  ? "text-[#181A1D] font-medium bg-[#F6E3E0]"
                  : "text-[#4A4E54] font-normal hover:text-[#181A1D]"
              }`}
            >
              <span
                aria-hidden="true"
                className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  active ? "bg-[#E94B3C]" : "bg-transparent"
                }`}
              />
              <span>{l.label}</span>
            </Link>
          );
        })}

        {VEILLE_COMMERCIALE ? null : (
          <Link
            to="/contact"
            onClick={onClose}
            data-testid="nav-mobile-cta-contact"
            className="mt-5 inline-flex items-center justify-center bg-[#E94B3C] text-white text-[13px] font-medium px-5 py-3 rounded-md hover:bg-[#B83A34] transition-colors"
          >
            Prendre contact
          </Link>
        )}
      </div>
    </div>
  );
}

export default function Navbar() {
  const { t } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;

  const links = VEILLE_COMMERCIALE
    ? [
        { id: "accueil", label: t("nav.accueil"), to: "/" },
        { id: "comprendre", label: t("nav.comprendre"), to: "/comprendre" },
        { id: "simon-marie", label: "À propos", to: "/simon-marie" },
        { id: "contact", label: "Contact", to: "/contact" },
      ]
    : [
        { id: "accueil", label: t("nav.accueil"), to: "/" },
        { id: "comprendre", label: t("nav.comprendre"), to: "/comprendre" },
        { id: "methode", label: t("nav.methode"), to: "/methode" },
        { id: "services", label: t("nav.services"), to: "/services" },
        { id: "droits", label: t("nav.droits"), to: "/cadre-juridique" },
        { id: "contact", label: "Contact", to: "/contact" },
      ];

  const isActive = (to) => {
    if (to === "/") return pathname === "/";
    if (to === "/comprendre") return pathname.startsWith("/comprendre");
    return pathname === to;
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (open) {
      const onKey = (e) => {
        if (e.key === "Escape") setOpen(false);
      };
      window.addEventListener("keydown", onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 border-b transition-colors duration-200 ${
        scrolled
          ? "bg-[#FAF9F7]/90 backdrop-blur-md border-[#E2DEDA]"
          : "bg-[#FAF9F7] border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between gap-8 h-[68px] lg:h-[78px]">
          <Brand />
          <DesktopNav links={links} isActive={isActive} />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden inline-flex items-center justify-center p-3 -mr-3 min-h-[44px] min-w-[44px] text-[#181A1D]"
            aria-label={t("nav.menu")}
            aria-expanded={open}
            data-testid="navbar-mobile-toggle"
          >
            {open ? (
              <X size={20} strokeWidth={1.5} />
            ) : (
              <Menu size={20} strokeWidth={1.5} />
            )}
          </button>
        </div>
      </div>

      {open && (
        <MobileDrawer links={links} isActive={isActive} onClose={() => setOpen(false)} />
      )}
    </header>
  );
}
